import { Button } from '@/components/ui/button';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import GoZigLogoDark from '@/assets/logos/goZig-logo-dark.svg';
import GoZigLogoLight from '@/assets/logos/goZig-logo-light.svg';
import { useUser } from '@/hooks/user/useUser.ts';
import { getLoginUrlWithMarketing, isModifiedClick, navigateToLogin } from '@/lib/loginUrl';
import { usePartnerLandingBranding } from '@/hooks/usePartnerLandingBranding';
import { shouldShowPartnerLanding } from '@/lib/partnerLandingUrl';
import { useTheme } from '@/contexts/ThemeContext/useTheme';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { SheetVisibilityContext } from '@/contexts/SheetVisibilityContext/SheetVisibilityContext';
import { cn } from '@/lib/utils';
import { shouldReloadForLocale } from '@/lib/locale';
import { HeaderNavLinks } from './HeaderNavLinks';
import { headerStrings } from './headerStrings';
import { NavMenuPanel } from './NavMenuPanel';
import { LANDING_SECTION_CONTAINER, LANDING_SECTION_PADDING_X } from '@/pages/Landing/landingSectionLayout';
import { MENU_BAR_HEIGHT_DESKTOP_PX, MENU_BAR_HEIGHT_PX } from '@/components/Menu/MenuUtils';

const SIGN_IN_ACTIVE_CLASSES = [
    'active:bg-secondary-accent active:border-secondary-accent active:text-foreground',
    'light:active:bg-gz-marine-800 light:active:border-gz-marine-800 light:active:text-background',
    'dark:active:bg-gz-marine-500 dark:active:border-gz-marine-500 dark:active:text-gz-neutral-800',
].join(' ');

const LOGO_LINK_FOCUS_CLASSES = [
    'rounded focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-secondary-accent/50',
    'light:focus-visible:ring-gz-marine-700/50 dark:focus-visible:ring-gz-marine-500/50',
].join(' ');

export const Menu = () => {
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useTranslation();
    const strings = headerStrings(t);

    const { user, isLoading: isLoadingUser } = useUser();
    const { theme } = useTheme();
    const isDesktop = useIsDesktop();
    const navigate = useNavigate();
    const location = useLocation();

    const setIsSheetOpen = useContext(SheetVisibilityContext)?.setIsSheetOpen;

    useEffect(() => {
        if (!setIsSheetOpen || !isOpen) return;

        setIsSheetOpen(true);
        return () => setIsSheetOpen(false);
    }, [isOpen, setIsSheetOpen]);

    // Radix returns focus to the trigger when the panel closes, which is right for Escape but fights
    // `ScrollToTop` after navigation — focusing the trigger scrolls the header back into view.
    const shouldRestoreFocus = useRef(true);

    const closeMenu = useCallback((navigated = true) => {
        if (isOpen) {
            shouldRestoreFocus.current = !navigated;
        }
        setIsOpen(false);
    }, [isOpen]);

    const onCloseAutoFocus = useCallback((event: Event) => {
        if (!shouldRestoreFocus.current) {
            event.preventDefault();
        }
        shouldRestoreFocus.current = true;
    }, []);

    const onLogout = useCallback(() => {
        window.location.href = '/api/logout';
    }, []);

    const onLogin = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
        if (isModifiedClick(event)) return;
        event.preventDefault();
        navigateToLogin();
    }, []);

    // Let ctrl/cmd/middle clicks fall through to the browser so the logo link can open in a new tab.
    const onNavigateHome = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
        if (isModifiedClick(event)) return;
        // A full load, so the server can send a signed-out Spanish reader to `/es` (see shouldReloadForLocale).
        if (shouldReloadForLocale()) return;
        event.preventDefault();
        navigate('../');
        closeMenu();
    }, [navigate, closeMenu]);

    const containerClasses = 'light:bg-gz-surface-light dark:bg-gz-blue-800 text-foreground';

    const menuBarHeight = `${isDesktop ? MENU_BAR_HEIGHT_DESKTOP_PX : MENU_BAR_HEIGHT_PX}px`;
    const isLandingHome = location.pathname === '/';
    const isPartnerLanding = shouldShowPartnerLanding(location.search);
    const partnerBranding = usePartnerLandingBranding(location.search);
    const showPartnerLogos = isLandingHome && isPartnerLanding && (partnerBranding?.logos.length ?? 0) > 0;
    /*
     * Matches the page-content gutter at every width. `DesktopContainer` and `SidebarLayout` only
     * clamp to `container` from `lg` up and run full-bleed below it, so the bar has to do the same:
     * a plain `container` starts stepping at 640px and pushed the bar's edge up to ~143px inboard of
     * the page content just under a breakpoint (ASA nav margins bug, reported at 768px).
     */
    const navInnerClasses = isLandingHome
        ? cn(LANDING_SECTION_CONTAINER, LANDING_SECTION_PADDING_X)
        : 'mx-auto w-full px-4 sm:px-6 lg:container lg:px-6';

    return (
        <>
            {/*
             * Dims the page behind the open menu. It stays clickable so it absorbs the click that
             * dismisses the menu, rather than letting it land on the page underneath.
             */}
            {isOpen && createPortal(
                <div className="fixed inset-0 z-[60] bg-gz-neutral-800/80" aria-hidden data-testid="menu-scrim" />,
                document.body,
            )}
            <header
                className={cn(
                    'sticky top-0 w-full border-b-0 shadow-md/5',
                    isOpen ? 'z-[65]' : 'z-40',
                    containerClasses,
                )}
            >
                {/*
                 * The panel hangs off the whole bar, not the trigger: Radix measures the gap from its
                 * anchor, and the trigger is a 36px button centred in a taller bar, so anchoring to it
                 * tucks the panel up inside the header. Anchoring to the bar also lines the panel's right
                 * edge up with the container edge rather than inside the trigger's padding.
                 * The root is always mounted so the bar markup isn't duplicated for signed-out users.
                 */}
                <Popover open={isOpen} onOpenChange={setIsOpen}>
                    <PopoverAnchor asChild>
                        <div
                            className={cn('flex min-h-0 items-center justify-between gap-2 lg:gap-8', navInnerClasses)}
                            style={{ height: menuBarHeight }}
                        >
                            <div className="flex min-w-0 flex-1 items-center gap-3 lg:gap-10">
                                <div className="min-w-0">
                                    {showPartnerLogos
                                        ? (
                                                <a
                                                    href="/"
                                                    className={cn('flex max-w-full min-w-0 items-center gap-2 sm:gap-3', LOGO_LINK_FOCUS_CLASSES)}
                                                    onClick={onNavigateHome}
                                                    aria-label={strings.partnerLogosLabel}
                                                    data-track-click="Header Logo Clicked"
                                                    data-track-location="header"
                                                    data-track-button-name="header-home-logo"
                                                >
                                                    {(partnerBranding?.logos ?? []).slice(0, 2).map(logo => (
                                                        <img
                                                            key={logo.key}
                                                            className={cn(
                                                                'w-auto min-w-0 shrink object-contain',
                                                                logo.key === 'kuder'
                                                                    ? 'h-6 lg:h-8'
                                                                    : 'h-8 lg:h-10',
                                                            )}
                                                            src={logo.src}
                                                            alt={logo.alt}
                                                            onError={(event) => {
                                                                event.currentTarget.src = GoZigLogoLight;
                                                                event.currentTarget.alt = strings.logoAlt;
                                                            }}
                                                        />
                                                    ))}
                                                </a>
                                            )
                                        : (
                                                <a
                                                    href="/"
                                                    className={cn('inline-flex max-w-full min-w-0', LOGO_LINK_FOCUS_CLASSES)}
                                                    data-track-click="Header Logo Clicked"
                                                    data-track-location="header"
                                                    data-track-button-name="header-home-logo"
                                                    onClick={onNavigateHome}
                                                >
                                                    <img
                                                        className="h-[34px] w-auto min-w-0 max-w-full shrink object-contain lg:h-[43px]"
                                                        src={theme === 'light' ? GoZigLogoLight : GoZigLogoDark}
                                                        alt={strings.logoAlt}
                                                    />
                                                </a>
                                            )}
                                </div>
                                {!isLoadingUser && <HeaderNavLinks isSignedIn={Boolean(user)} strings={strings.nav} />}
                            </div>
                            {user && (
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        className={cn(
                                            'inline-flex size-9 shrink-0 items-center justify-center rounded-lg border-2 transition-colors',
                                            'light:border-gz-marine-800 light:text-gz-marine-800',
                                            'dark:border-gz-neutral-200 dark:text-gz-neutral-100',
                                            'light:hover:bg-gz-tan-350 dark:hover:bg-gz-blue-600',
                                            'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-secondary-accent/50',
                                            'light:focus-visible:ring-gz-marine-700/50 dark:focus-visible:ring-gz-marine-500/50',
                                        )}
                                        aria-label={isOpen ? strings.closeMenu : strings.menu}
                                        data-testid="menu-trigger"
                                        data-track-click="Header Menu Button Clicked"
                                        data-track-location="menu"
                                        data-track-button-name="nav-menu-toggle"
                                    >
                                        <span aria-hidden className="material-symbols-rounded !text-2xl">
                                            {isOpen ? 'close' : 'dehaze'}
                                        </span>
                                    </button>
                                </PopoverTrigger>
                            )}
                            {!isLoadingUser && !user && (
                                <Button
                                    asChild
                                    variant="outline"
                                    size="default"
                                    className={cn('h-9 shrink-0 rounded-full px-4', SIGN_IN_ACTIVE_CLASSES)}
                                    data-track-click="Sign In Button Clicked"
                                    data-track-button-name="sign-in"
                                >
                                    <a href={getLoginUrlWithMarketing()} onClick={onLogin} aria-label={strings.signIn.label}>
                                        {strings.signIn.text}
                                    </a>
                                </Button>
                            )}
                        </div>
                    </PopoverAnchor>
                    {user && (
                        <PopoverContent
                            align="end"
                            sideOffset={8}
                            collisionPadding={8}
                            aria-label={strings.navigationMenuLabel}
                            onCloseAutoFocus={onCloseAutoFocus}
                            data-testid="menu-panel"
                            className={cn(
                                'w-[290px] rounded-[32px] p-4 lg:w-[316px]',
                                // Not in the design, but the panel is taller than a landscape phone without it.
                                'max-h-(--radix-popover-content-available-height) overflow-y-auto',
                                'light:bg-gz-surface-light dark:bg-gz-blue-800',
                                'border-none shadow-[0_4px_16px_rgba(14,13,12,0.2)]',
                            )}
                        >
                            <NavMenuPanel user={user} onNavigate={closeMenu} onLogout={onLogout} />
                        </PopoverContent>
                    )}
                </Popover>
            </header>
        </>
    );
};
